/**
 * Print Depot — configurator conditional logic
 *
 * Shows and hides configuration controls based on what else is selected.
 * The rules live in the product's metafield as show_when="key=Value", so
 * adding a condition is a data edit — there is no per-product logic here.
 *
 * A key can name either another configurator control or a variant option, so
 * "printed_sides=Double-Sided" works whether Printed Sides is a Shopify option
 * or one of these controls.
 *
 * A hidden control must not block Add to Cart, so its required flag is lifted
 * while it is out of view and restored when it returns. Shopify would
 * otherwise refuse to submit on a field the customer cannot see — the same
 * trap the artwork back row hit.
 *
 * Delegated from the document: the variant picker sits in its own form and
 * Horizon morphs it on every change, so anything bound directly to those
 * inputs would not survive the first selection.
 */
(() => {
  const norm = (s) => (s || '').trim().toLowerCase();

  /** Current value for a key, from a configurator control or a variant option. */
  const valueFor = (scope, key) => {
    const own = scope.querySelector(`[data-pd-config-input="${CSS.escape(key)}"]`);
    if (own) {
      if (own.type === 'checkbox') return own.checked ? own.value : '';
      if (own.type === 'radio') {
        const picked = scope.querySelector(`input[data-pd-config-input="${CSS.escape(key)}"]:checked`);
        return picked ? picked.value : '';
      }
      return own.value;
    }

    // Fall back to a Shopify variant option of the same name.
    for (const input of scope.querySelectorAll('input[data-option-name]:checked')) {
      if (norm(input.dataset.optionName).replace(/[^a-z0-9]+/g, '_') === norm(key)) return input.value;
    }
    for (const select of scope.querySelectorAll('select[data-option-name]')) {
      if (norm(select.dataset.optionName).replace(/[^a-z0-9]+/g, '_') === norm(key)) return select.value;
    }
    return null;
  };

  const sync = () => {
    for (const group of document.querySelectorAll('[data-pd-show-when]')) {
      const scope = group.closest('.shopify-section') || document;
      const [key, expected] = (group.dataset.pdShowWhen || '').split('=');
      if (!key) continue;

      const actual = valueFor(scope, key.trim());
      // Unknown key: leave the control as the template rendered it rather
      // than guessing at whether it belongs on screen.
      if (actual === null) continue;

      const show = norm(actual) === norm(expected);
      group.hidden = !show;

      for (const field of group.querySelectorAll('[data-pd-config-input]')) {
        if (show) {
          if (field.dataset.pdWasRequired === '1') field.required = true;
        } else if (field.required) {
          field.dataset.pdWasRequired = '1';
          field.required = false;
        }
      }
    }
  };

  /**
   * Say why Add to Cart did nothing.
   *
   * Horizon's button is type="button", so the browser never runs its own
   * validation pass, and the theme's click handler bails on checkValidity()
   * without a word. A customer who has not ticked the artwork confirmation
   * would click and watch nothing happen.
   *
   * Capture phase, so this runs before the theme's handler returns.
   * reportValidity focuses the offending field and shows the browser's own
   * message; nothing is prevented, so a valid form proceeds untouched.
   */
  document.addEventListener(
    'click',
    (event) => {
      const el = event.target;
      if (!(el instanceof Element)) return;
      const button = el.closest('.add-to-cart-button');
      if (!button) return;

      const form = button.closest('form');
      if (!form || form.checkValidity()) return;

      const invalid = form.querySelector(':invalid');
      // A hidden control should never be what blocks the sale. sync() lifts
      // required on hidden groups, so this is a backstop rather than the fix.
      if (invalid instanceof HTMLElement && invalid.closest('[hidden]')) return;

      form.reportValidity();
      invalid?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    },
    true
  );

  document.addEventListener('change', (event) => {
    if (event.target instanceof Element) sync();
  });
  document.addEventListener('shopify:product:select', sync);
  document.addEventListener('shopify:section:load', sync);
  document.addEventListener('DOMContentLoaded', sync);

  sync();
})();
